import React, { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { isStandalone, isIOS } from "@/lib/native";

const KEY = "mm369_install_dismissed_at";
const SNOOZE_DAYS = 14;
const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    const at = Number(localStorage.getItem(KEY) || 0);
    if (at && Date.now() - at < SNOOZE_DAYS * 86400000) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = setTimeout(() => { if (isIOS()) { setIos(true); setShow(true); } }, 6000);
    const onInstalled = () => { setShow(false); localStorage.setItem(KEY, String(Date.now())); };
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); clearTimeout(t); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(KEY, String(Date.now())); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false); else dismiss();
    setDeferred(null);
  };
  if (!show) return null;

  return (
    <div data-testid="install-prompt" className="install-prompt lg:hidden print:hidden fixed inset-x-3 z-50 bg-zinc-900 text-white shadow-2xl border border-zinc-700 animate-in slide-in-from-bottom-4 duration-300" style={{ bottom: `calc(${document.body.classList.contains("has-tabbar") ? "76px" : "12px"} + env(safe-area-inset-bottom))` }}>
      <button data-testid="install-dismiss" onClick={dismiss} aria-label="Dismiss" className="absolute top-2 right-2 p-1 text-zinc-400 hover:text-white"><X size={14} /></button>
      <div className="p-4 flex gap-3">
        <img src="/icon-192.png" alt="" className="w-12 h-12 shrink-0 rounded-xl bg-white/10" />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold tracking-tight leading-tight pr-5">Add Mind Maker to your home screen</div>
          <div className="text-xs text-zinc-400 mt-0.5">One-tap access to billing, POS and your portal — works offline too.</div>
          {ios ? (
            <ol className="mt-2 text-xs text-zinc-200 space-y-1" data-testid="install-ios-steps">
              <li className="flex items-center gap-1.5"><span className="w-4 h-4 grid place-items-center bg-white text-zinc-900 text-[10px] font-bold">1</span> Tap <Share size={12} className="inline" /> <b>Share</b> in Safari's toolbar</li>
              <li className="flex items-center gap-1.5"><span className="w-4 h-4 grid place-items-center bg-white text-zinc-900 text-[10px] font-bold">2</span> Choose <PlusSquare size={12} className="inline" /> <b>Add to Home Screen</b></li>
              <li><Link to="/install" data-testid="install-guide-link" onClick={dismiss} className="underline decoration-dotted text-zinc-300">Full guide with pictures →</Link></li>
            </ol>
          ) : (
            <div className="flex gap-2 mt-3">
              <button data-testid="install-btn" onClick={install} className="flex-1 bg-white text-zinc-900 text-xs font-semibold px-3 py-2 flex items-center justify-center gap-1 hover:bg-zinc-100"><Download size={13} /> Install app</button>
              <button data-testid="install-later" onClick={dismiss} className="text-xs font-semibold px-3 py-2 border border-zinc-600 hover:bg-zinc-800">Not now</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
